import * as React from 'react';
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import * as Device from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  useListNotificationsQuery,
  ListNotificationsQuery,
  NotificationTypeEnum,
  useMarkNotificationAsReadMutation,
} from '@graphql';
import { useSession } from '@hooks';

interface NotificationDrawerProps {
  handleOpenDialog: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseDrawer: () => void;
}

export function NotificationDrawer(props: Readonly<NotificationDrawerProps>) {
  const { handleCloseDrawer } = props;
  const theme = useTheme();
  const { account } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('notification');

  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();

  /**
   * Cache-only query to fetch notifications
   * when new notifications are received via socket in NotificationButton.tsx component,
   * the cache will also be updated regardless
   */
  const { data } = useListNotificationsQuery({
    skip: !account,
    fetchPolicy: 'cache-and-network',
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const notificationId = event.currentTarget.getAttribute('data-id');
    const from = location.search?.length ? `${location.pathname}?${location.search}` : location.pathname;
    const navigateOptions = { preventScrollReset: true, state: { from } };

    if (notificationId) {
      const notification = data?.notifications.find((n) => n.id === notificationId);

      if (notification?.link) {
        navigate(notification?.link, navigateOptions);
      }

      markNotificationAsRead({ variables: { notificationId } });
    }
  };

  if (!account) {
    return null;
  }

  return (
    <Box
      sx={{
        width: Device.isMobile ? '100vw' : 400,
        '& .MuiListItemButton-root': {
          color: theme.palette.text.secondary,
          '& .MuiListItemIcon-root, & .MuiListItemText-root': {
            color: theme.palette.text.secondary,
          },
          '&:hover': {
            color: theme.palette.text.primary,
            '& .MuiListItemIcon-root, & .MuiListItemText-root': {
              color: theme.palette.text.primary,
            },
          },
        },
        '& .MuiDivider-root': {
          backgroundColor: theme.palette.grey[700],
        },
      }}
      role="presentation"
    >
      <List
        subheader={
          <ListSubheader
            component="div"
            id="notifications-subheader"
            sx={{ zIndex: 2, borderBottom: `1px solid ${theme.palette.grey[600]}` }}
          >
            <Stack
              direction="row"
              justifyContent={Device.isMobile ? 'space-between' : 'flex-end'}
              alignItems="center"
              py={2}
            >
              {Device.isMobile && (
                <Button
                  startIcon={<theme.icons.back />}
                  onClick={handleCloseDrawer}
                  size="small"
                  sx={{ color: 'white' }}
                >
                  {t('back', { ns: 'cta' })}
                </Button>
              )}
              <Typography variant="h6" fontWeight="bold">
                {t('Notifications')}
              </Typography>
            </Stack>
          </ListSubheader>
        }
        sx={{ '& ul': { padding: 0 } }}
      >
        <Stack
          spacing={0}
          divider={<Divider flexItem />}
          sx={{
            overflowY: 'scroll',
          }}
        >
          {data?.notifications.length ? (
            data.notifications.map((notification) => (
              <Stack
                key={notification.id}
                spacing={1}
                data-id="notification"
                notification-id={notification.id}
              >
                <ListItem disablePadding>
                  <ListItemButton width="100%" onClick={handleClick} data-id={notification.id}>
                    <Stack direction="row" spacing={2} alignItems="center" width="100%">
                      <NotificationIcon
                        source={notification.source}
                        type={notification.type}
                        count={notification.count}
                      />
                      <Stack width="100%" spacing={0.5}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={theme.palette.text[notification.count ? 'primary' : 'secondary']}
                          fontSize={{ xs: '0.6rem', sm: '.8rem' }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={notification.count ? 'bold' : 'normal'}
                          color={theme.palette.text[notification.count ? 'primary' : 'secondary']}
                          fontSize=".7rem"
                        >
                          {notification.subtitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={theme.palette.text[notification.count ? 'primary' : 'secondary']}
                          fontSize={{ xs: '0.5rem', sm: '.6rem' }}
                        >
                          {moment(notification.createdAt).fromNow()}
                        </Typography>
                      </Stack>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              </Stack>
            ))
          ) : (
            <Stack>
              <ListItem disablePadding>
                <ListItemButton width="100%" disabled>
                  <Stack direction="row" spacing={1} alignItems="center" width="100%">
                    <Typography variant="body2" fontWeight="bold">
                      {t('No notification')}
                    </Typography>
                  </Stack>
                </ListItemButton>
              </ListItem>
              <Divider sx={{ backgroundColor: theme.palette.grey[800] }} variant="middle" flexItem />
            </Stack>
          )}
        </Stack>
      </List>
    </Box>
  );
}
interface NotificationIconProps {
  source: ListNotificationsQuery['notifications'][0]['source'];
  type: NotificationTypeEnum;
  count: number;
}

function NotificationIcon(props: Readonly<NotificationIconProps>) {
  const { type, count, source } = props;
  const theme = useTheme();

  const icons = {
    [NotificationTypeEnum.Booking]: theme.icons.booking,
    [NotificationTypeEnum.Event]: theme.icons.event,
    [NotificationTypeEnum.Friendship]: theme.icons.people,
    [NotificationTypeEnum.Message]: theme.icons.send,
  };

  const Icon = icons[type] ?? theme.icons.notification;
  const size = { width: 30, height: 30 };

  return (
    <Badge
      color="error"
      badgeContent={count}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      {source && ![NotificationTypeEnum.Event].includes(type) ? (
        <Avatar src={source.avatar?.url} alt={source.name} sx={size} />
      ) : (
        <Icon
          sx={{
            ...size,
            color: theme.palette.text[count === 0 ? 'secondary' : 'primary'],
          }}
        />
      )}
    </Badge>
  );
}
